import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { 
  Search, FolderOpen, TrendingUp, Tag, Hash, 
  Eye, X, ChevronDown 
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';

export const BlogOverviewSidebar = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeCategory = searchParams.get('category');
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  
  // Collapsible state for categories
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchTerm(value);
    setShowResults(value.length >= 2);
  }, 300);

  // Search Query
  const { data: searchResults = [] } = useQuery({
    queryKey: ['blog-search', debouncedSearchTerm],
    queryFn: async () => {
      if (debouncedSearchTerm.length < 2) return [];
      const { data } = await supabase
        .from('blog_posts')
        .select('id, title, slug, category')
        .or(`title.ilike.%${debouncedSearchTerm}%,content.ilike.%${debouncedSearchTerm}%`)
        .eq('published', true)
        .limit(5);
      return data || [];
    },
    enabled: debouncedSearchTerm.length >= 2,
    staleTime: 2 * 60 * 1000,
  });

  // Categories Query
  const { data: categories = [] } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('category')
        .eq('published', true);
      
      const categoryCount = data?.reduce((acc, post) => {
        if (post.category) {
          acc[post.category] = (acc[post.category] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);
      
      return Object.entries(categoryCount || {})
        .map(([name, count]) => ({
          name,
          slug: name.toLowerCase().replace(/\s+/g, '-'),
          count
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
    },
    staleTime: 5 * 60 * 1000,
  });

  // Popular Posts Query
  const { data: popularPosts = [] } = useQuery({
    queryKey: ['popular-posts'],
    queryFn: async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('id, title, slug, featured_image, views')
        .eq('published', true)
      .order('views', { ascending: false })
      .limit(3);
      return data || [];
    },
    staleTime: 10 * 60 * 1000,
  });

  // Tags Query
  const { data: tags = [] } = useQuery({
    queryKey: ['blog-tags'],
    queryFn: async () => {
      const { data } = await supabase
        .from('blog_posts')
        .select('tags')
        .eq('published', true);
      
      const allTags = data?.flatMap(post => post.tags || []);
      const tagCount = allTags?.reduce((acc, tag) => {
        acc[tag] = (acc[tag] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      return Object.entries(tagCount || {})
        .map(([name, count]) => ({
          name,
          slug: name.toLowerCase().replace(/\s+/g, '-'),
          count
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 12);
    },
    staleTime: 5 * 60 * 1000,
  });


  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleResultClick = (slug: string) => {
    navigate(`/blog/${slug}`);
    setSearchTerm('');
    setShowResults(false);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setShowResults(false);
  };

  return (
    <aside 
      className="space-y-6" 
      aria-label="Blog Sidebar" 
      role="complementary"
    >
      {/* 1. SEARCH COMPONENT */}
      <section aria-label="Suche">
        <div className="bg-card/40 backdrop-blur-xl border border-border/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-accent/50">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Search className="w-5 h-5 text-accent" />
            Suche
          </h3>
          
          <div className="relative">
            <span className="sr-only">Blog durchsuchen</span>
            <input
              type="text"
              placeholder="Blog durchsuchen..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full px-4 py-2.5 pl-10 pr-10 bg-background/50 border border-border/50 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-all"
              aria-label="Suche im Blog"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Suche löschen"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            
            {/* Search Results Dropdown */}
            {showResults && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card/95 backdrop-blur-xl border border-border/30 rounded-lg shadow-xl overflow-hidden z-10">
                <span aria-live="polite" className="sr-only">
                  {searchResults.length} Ergebnisse gefunden
                </span>
                {searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => handleResultClick(result.slug)}
                    className="w-full px-4 py-3 text-left hover:bg-accent/10 transition-colors border-b border-border/20 last:border-b-0"
                  >
                    <div className="font-medium text-sm line-clamp-1 hover:text-accent transition-colors">
                      {result.title}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {result.category}
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {showResults && searchResults.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-card/95 backdrop-blur-xl border border-border/30 rounded-lg shadow-xl p-4 z-10">
                <p className="text-sm text-muted-foreground text-center">
                  Keine Ergebnisse gefunden
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 2. CATEGORIES COMPONENT - Collapsible on Mobile/Tablet */}
      <nav aria-label="Blog Kategorien">
        <div className="bg-card/40 backdrop-blur-xl border border-border/30 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:border-accent/50">
          <Collapsible
            open={isCategoriesOpen}
            onOpenChange={setIsCategoriesOpen}
            className="w-full"
          >
            <CollapsibleTrigger className="w-full p-6 flex items-center justify-between hover:bg-accent/5 transition-colors lg:pointer-events-none">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-accent" />
                Kategorien
              </h3>
              <ChevronDown 
                className={`w-5 h-5 text-accent transition-transform duration-300 lg:hidden ${
                  isCategoriesOpen ? 'rotate-180' : ''
                }`}
              />
            </CollapsibleTrigger>
            
            <CollapsibleContent className="lg:block">
              <div className="px-6 pb-6 space-y-2">
                {categories.map((category) => (
                  <Link
                    key={category.name}
                    to={`/blog?category=${category.slug}`}
                    className={`flex items-center justify-between p-3 rounded-lg transition-all duration-300 group ${
                      activeCategory === category.slug
                        ? 'bg-accent/20 border border-accent/50 shadow-sm'
                        : 'bg-background/30 hover:bg-accent/10 border border-transparent'
                    }`}
                  >
                    <span className="font-medium text-sm group-hover:text-accent transition-colors">
                      {category.name}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      activeCategory === category.slug
                        ? 'bg-accent/30 text-accent'
                        : 'bg-muted/50 text-muted-foreground'
                    }`}>
                      {category.count}
                    </span>
                  </Link>
                ))}
              </div>
              
              {/* Link zur Kategorien-Übersichtsseite */}
              <div className="px-6 pb-4 border-t border-border/20 pt-4">
                <Link 
                  to="/blog/kategorien"
                  className="text-sm text-accent hover:underline flex items-center justify-center gap-2 font-medium"
                >
                  Alle Kategorien anzeigen →
                </Link>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </nav>

      {/* 3. POPULAR POSTS COMPONENT - Hidden on Mobile/Tablet */}
      <section aria-label="Beliebte Artikel" className="hidden lg:block">
        <div className="bg-card/40 backdrop-blur-xl border border-border/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-accent/50">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-accent" />
            Beliebte Artikel
          </h3>
          
          <div className="space-y-4">
            {popularPosts.map((post, index) => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="block p-2 rounded-lg hover:bg-accent/10 transition-colors group"
              >
                <h4 className="text-sm font-medium line-clamp-2 group-hover:text-accent transition-colors">
                  {post.title}
                </h4>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 4. TAGS CLOUD COMPONENT - Hidden on Mobile/Tablet */}
      <nav aria-label="Tags" className="hidden lg:block">
        <div className="bg-card/40 backdrop-blur-xl border border-border/30 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-accent/50">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Tag className="w-5 h-5 text-accent" />
            Tags
          </h3>
          
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <Link
                key={tag.name}
                to={`/blog?tag=${tag.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background/50 border border-border/50 rounded-full text-xs font-medium hover:bg-accent/10 hover:border-accent/50 hover:text-accent transition-all duration-300 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-accent/50"
              >
                <Hash className="w-3 h-3" />
                {tag.name}
                <span className="text-[10px] text-muted-foreground">({tag.count})</span>
              </Link>
            ))}
          </div>
        </div>
      </nav>

    </aside>
  );
};
